import { addToWindowRef, voidFunction } from '@avantia/client-and-server-utilities';
import { HpWindowAnswerData } from '../actions/getAnswersActionTypes';
import { HpClientDebugFlags } from '../clientDebugFlags';
import { logInfo } from '../clientLogging';
import { registerConfigurationAction } from '../config/registerClientConfig';
import { createCookieProxy } from '../tools/clientCookies';
import {
  HpCookies,
  HpCustomHashLoadHandler,
  HpLocationTransition,
  HpPageLoadDataProps,
  HpPageLoadDataResponse
} from '../types';
import { getAllHashKeys, getHashRecord } from './handlerUtilities';
import { getRoutingHandler } from './routingStub';

const customLoadDataHandlers: HpCustomHashLoadHandler<unknown>[] = [];

let timesCalled = 0;
let locationTransition: HpLocationTransition | null;
let productIdDefault = '';

registerConfigurationAction((config) => {
  ({ productIdDefault } = config);
});

export function pageLoadDataHandler(props: HpPageLoadDataProps): HpPageLoadDataResponse | undefined {
  const { location } = props;
  let loadData: HpPageLoadDataResponse | null;
  const { hash, pathname, search } = location;
  const currentHref = `${pathname}${search}${hash}`;

  timesCalled++;

  if (locationTransition && (currentHref === locationTransition.from || currentHref !== locationTransition.to)) {
    loadData = null;
  } else {
    loadData = pageLoadData<HpPageLoadDataResponse>(props, timesCalled);
    if (loadData && loadData.newUrl) {
      locationTransition = { from: currentHref, to: loadData.newUrl };
      if (locationTransition.from === locationTransition.to && timesCalled > 1) {
        locationTransition = null;
        loadData = null;
      }
    }
  }

  const messagePrefix = `Running 'pageLoadDataHandler' (${timesCalled})`;
  logInfo({ message: messagePrefix, location, locationTransition, loadData }, HpClientDebugFlags.PageLoadDataHandler);

  return loadData || undefined;
}

export function clearLocationTransition(): void {
  locationTransition = null;
}

export function isHashAnErrorLink(hash: string): boolean {
  return !!hash && hash.startsWith('#QP');
}

function pageLoadData<HashMapT extends Record<string, any>>(
  { location, answersIdLoaded, questionSetsLoadedCount, cookies, productId }: HpPageLoadDataProps,
  timesCalled: number
): HpPageLoadDataResponse {
  const { hash, pathname: path, search } = location;
  const cookieProxy: HpCookies = cookies || createCookieProxy();
  const isErrorLink: boolean = isHashAnErrorLink(hash);
  let response: HpPageLoadDataResponse | undefined;
  if (!isErrorLink || timesCalled === 1) {
    const routing = getRoutingHandler();
    const hashMap = getHashRecord<HashMapT>(hash, getAllHashKeys<HashMapT>()) as HashMapT;
    const rootUrl = routing.isJourneyRoute(location.pathname, { ignoreEmpty: true, ignoreSlash: true })
      ? location.pathname
      : routing.getInitialRoute(productId);

    for (const customLoadDataHandler of customLoadDataHandlers) {
      const { id, canHandleHash, needToLoadData } = customLoadDataHandler as HpCustomHashLoadHandler<HashMapT>;
      if (canHandleHash({ hashMap, location, rootUrl })) {
        const loadData = needToLoadData({
          answersIdLoaded,
          cookies: cookieProxy,
          hashMap,
          location,
          path,
          productIdDefault,
          questionSetsLoadedCount,
          rootUrl,
          search
        });

        const resp = loadData as HpPageLoadDataResponse;
        resp.handlerId = id;
        const data: HpWindowAnswerData = { answerData: undefined };
        addToWindowRef(data as any);
        response = resp;
        break;
      }
    }
  }

  if (!response) {
    response = {
      handlerId: undefined,
      load: 'none',
      needs: 'none',
      newUrl: undefined,
      productId: '',
      setCustomAnswers: voidFunction as any
    };
  }

  return response;
}

export function registerCustomHashAndAnswersHandler<HashMapT extends Record<string, any>>(
  data: HpCustomHashLoadHandler<HashMapT> | undefined
): void {
  if (data) {
    customLoadDataHandlers.push(data as HpCustomHashLoadHandler<unknown>);
  }
}

export function clearAllLoadDataHandlers(): void {
  timesCalled = 0;
  customLoadDataHandlers.length = 0;
  locationTransition = null;
}
